import React, { useContext, useMemo, useRef, useState } from 'react';
import LazyTable from 'app/component/lazy-table';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { ToastContext } from 'app/context/ToastContext';
import { formatFromLocalDate, formatDateFromInstant } from 'app/shared/util/date-utils';
import {
  cancelUpomienka,
  downloadUpomienkaPdf,
  downloadUpomienkaDocx,
  markUpomienkaSent,
  prepareUpomienky,
  sendUpomienkaEmail,
  sendUpomienkaEmailBulk,
} from 'app/api/upomienkaApi';

type LazyTableHandle = {
  reloadData: () => any;
};

const STATUS_LABELS: Record<string, { label: string; severity: 'info' | 'success' | 'warning' | 'danger' }> = {
  PREPARED: { label: 'Pripravená', severity: 'info' },
  SENT: { label: 'Odoslaná', severity: 'success' },
  CANCELLED: { label: 'Zrušená', severity: 'warning' },
};

const formatAmount = (v: any) => {
  if (v === null || v === undefined) return '';
  return `${Number(v).toFixed(2).replace('.', ',')} €`;
};

const defaultSubjectFor = (row: any) =>
  `Upomienka ${row?.level ?? ''}. úrovne${row?.insuranceCompanyName ? ' – ' + row.insuranceCompanyName : ''}`;

const defaultBodyFor = (row: any) => {
  const contractNr = row?.contractNr || row?.proposalNr || '';
  return (
    `<p>Dobrý deň,</p>` +
    `<p>v prílohe Vám zasielame upomienku ${row?.level ?? ''}. úrovne k zmluve č. ${contractNr}.</p>` +
    `<p>S pozdravom,<br/>NFA</p>`
  );
};

const defaultBulkBody = () =>
  `<p>Dobrý deň,</p>` +
  `<p>v prílohe Vám zasielame upomienku k Vašej zmluve.</p>` +
  `<p>S pozdravom,<br/>NFA</p>`;

const UpomienkaTable: React.FC = () => {
  const { showSuccess, showError } = useContext(ToastContext);
  const tableRef = useRef<LazyTableHandle>(null);
  const [loading, setLoading] = useState(false);

  const [selected, setSelected] = useState<any[]>([]);

  // single-row send dialog
  const [singleDialog, setSingleDialog] = useState<{ open: boolean; row: any; email: string; subject: string; message: string }>({
    open: false,
    row: null,
    email: '',
    subject: '',
    message: '',
  });

  // bulk send dialog
  const [bulkDialog, setBulkDialog] = useState<{ open: boolean; subject: string; message: string }>({
    open: false,
    subject: 'Upomienka',
    message: defaultBulkBody(),
  });

  const reload = () => tableRef.current?.reloadData();

  const onPrepare = () => {
    setLoading(true);
    prepareUpomienky()
      .then(r => {
        const count = Array.isArray(r.data) ? r.data.length : 0;
        showSuccess('Hotovo', `Pripravených ${count} upomienok.`);
        reload();
      })
      .catch(e => showError('Chyba', e?.response?.data?.detail || 'Príprava upomienok zlyhala.'))
      .finally(() => setLoading(false));
  };

  const onDownloadPdf = (data: any) => {
    setLoading(true);
    downloadUpomienkaPdf(data.id)
      .catch(e => showError('Chyba', e?.response?.data?.detail || 'Stiahnutie PDF zlyhalo.'))
      .finally(() => setLoading(false));
  };

  const onDownloadDocx = (data: any) => {
    setLoading(true);
    downloadUpomienkaDocx(data.id)
      .catch(e => showError('Chyba', e?.response?.data?.detail || 'Stiahnutie DOCX zlyhalo.'))
      .finally(() => setLoading(false));
  };

  const onMarkSent = (data: any) => {
    confirmDialog({
      message: `Označiť upomienku ${data.level}. úrovne ako odoslanú klientovi ${data.clientName || ''}?`,
      header: 'Potvrdenie odoslania',
      icon: 'pi pi-send',
      acceptLabel: 'Označiť ako odoslanú',
      rejectLabel: 'Zrušiť',
      accept() {
        setLoading(true);
        markUpomienkaSent(data.id, data.clientEmail || undefined)
          .then(() => {
            showSuccess('Hotovo', 'Upomienka označená ako odoslaná.');
            reload();
          })
          .catch(e => showError('Chyba', e?.response?.data?.detail || 'Aktualizácia stavu zlyhala.'))
          .finally(() => setLoading(false));
      },
    });
  };

  const onCancel = (data: any) => {
    confirmDialog({
      message: `Zrušiť pripravenú upomienku pre ${data.clientName || ''}?`,
      header: 'Zrušiť upomienku',
      icon: 'pi pi-times',
      acceptLabel: 'Zrušiť upomienku',
      rejectLabel: 'Späť',
      accept() {
        setLoading(true);
        cancelUpomienka(data.id)
          .then(() => {
            showSuccess('Hotovo', 'Upomienka zrušená.');
            reload();
          })
          .catch(e => showError('Chyba', e?.response?.data?.detail || 'Zrušenie zlyhalo.'))
          .finally(() => setLoading(false));
      },
    });
  };

  // --- single email ---------------------------------------------------------

  const openSingleEmailDialog = (data: any) => {
    setSingleDialog({
      open: true,
      row: data,
      email: data.clientEmail || '',
      subject: defaultSubjectFor(data),
      message: defaultBodyFor(data),
    });
  };

  const closeSingleEmailDialog = () => setSingleDialog(s => ({ ...s, open: false, row: null }));

  const onSendSingleEmail = () => {
    const { row, email, subject, message } = singleDialog;
    if (!row) return;
    if (!email?.trim()) {
      showError('Chýba e-mail', 'Zadajte prosím e-mailovú adresu príjemcu.');
      return;
    }
    setLoading(true);
    sendUpomienkaEmail(row.id, email.trim(), subject, message)
      .then(() => {
        showSuccess('Odoslané', `Upomienka odoslaná na ${email.trim()}.`);
        closeSingleEmailDialog();
        reload();
      })
      .catch(e => showError('Chyba', e?.response?.data?.detail || 'Odoslanie e-mailu zlyhalo.'))
      .finally(() => setLoading(false));
  };

  // --- bulk email -----------------------------------------------------------

  const sendableSelected = useMemo(
    () => (selected || []).filter(r => r?.status === 'PREPARED' && !!r?.clientEmail?.trim()),
    [selected],
  );
  const skippedSelected = useMemo(() => (selected || []).filter(r => !sendableSelected.includes(r)), [selected, sendableSelected]);

  const openBulkDialog = () => {
    if (!selected.length) {
      showError('Vyberte upomienky', 'Najprv označte aspoň jednu upomienku.');
      return;
    }
    if (!sendableSelected.length) {
      showError('Nič na odoslanie', 'Vybrané upomienky nemajú stav „Pripravená" alebo nemajú e-mail klienta.');
      return;
    }
    setBulkDialog(s => ({ ...s, open: true }));
  };

  const closeBulkDialog = () => setBulkDialog(s => ({ ...s, open: false }));

  const onSendBulk = () => {
    const ids = sendableSelected.map(r => r.id);
    if (!ids.length) return;
    setLoading(true);
    sendUpomienkaEmailBulk(ids, bulkDialog.subject, bulkDialog.message)
      .then(r => {
        const succeeded = r.data?.succeededIds?.length || 0;
        const failed = r.data?.failures?.length || 0;
        if (failed === 0) {
          showSuccess('Odoslané', `Úspešne odoslaných ${succeeded} upomienok.`);
        } else {
          const firstReason = r.data?.failures?.[0]?.reason || '';
          showError('Čiastočný úspech', `Odoslaných ${succeeded}, zlyhalo ${failed}. ${firstReason}`);
        }
        closeBulkDialog();
        setSelected([]);
        reload();
      })
      .catch(e => showError('Chyba', e?.response?.data?.detail || 'Hromadné odoslanie zlyhalo.'))
      .finally(() => setLoading(false));
  };

  // --- formatters -----------------------------------------------------------

  const statusFormatter = (_: any, v: any) => {
    const cfg = STATUS_LABELS[v?.status];
    if (!cfg) return v?.status;
    return <Tag value={cfg.label} severity={cfg.severity} />;
  };

  const levelFormatter = (_: any, v: any) => {
    if (!v?.level) return '';
    return v.level === 3 ? `${v.level}. (posledná)` : `${v.level}.`;
  };

  return (
    <>
      <ConfirmDialog />
      {LazyTable({
        ref: tableRef,
        title: 'Upomienky',
        initialSortField: 'preparedAt',
        initialSortDirection: 'desc',
        createUrl: '/',
        serverUrl: '/api/upomienky',
        editActionAllowed: false,
        externalLoading: loading,
        globalFilterFields: ['contractNr', 'proposalNr', 'clientName', 'insuranceCompanyName'],
        onSelectionChange: setSelected,
        selectedItems: selected,
        additionalHeaderButtons: (
          <span>
            <div className="flex justify-content-between gap-2">
              <Button label="Pripraviť nové upomienky" icon="pi pi-search" severity="info" onClick={onPrepare} loading={loading} />
              <Button
                label={`Odoslať vybrané e-mailom${selected.length ? ` (${selected.length})` : ''}`}
                icon="pi pi-envelope"
                severity="success"
                disabled={!selected.length}
                onClick={openBulkDialog}
                loading={loading}
              />
            </div>
          </span>
        ),
        columns: [
          { field: 'id', header: 'ID', sortable: true },
          { field: 'insuranceCompanyName', header: 'Spoločnosť', sortable: true },
          { field: 'contractNr', header: 'Č. zmluvy', sortable: true },
          { field: 'clientName', header: 'Klient', sortable: false },
          { field: 'clientEmail', header: 'E-mail', sortable: false },
          { field: 'level', header: 'Úroveň', sortable: true, formatter: levelFormatter },
          { field: 'amountDue', header: 'Nedoplatok', sortable: true, formatter: (_, v) => formatAmount(v?.amountDue) },
          { field: 'periodFrom', header: 'Obdobie od', sortable: true, formatter: formatFromLocalDate },
          { field: 'periodTo', header: 'Obdobie do', sortable: true, formatter: formatFromLocalDate },
          { field: 'status', header: 'Stav', sortable: true, formatter: statusFormatter },
          { field: 'preparedAt', header: 'Pripravená', sortable: true, formatter: formatDateFromInstant },
          { field: 'sentAt', header: 'Odoslaná', sortable: true, formatter: formatDateFromInstant },
        ],
        additionalActions: [
          {
            icon: 'pi pi-file-pdf',
            tooltip: 'Stiahnuť PDF',
            onClick: onDownloadPdf,
          },
          {
            icon: 'pi pi-file-word',
            tooltip: 'Stiahnuť DOCX',
            onClick: onDownloadDocx,
          },
          {
            icon: 'pi pi-envelope',
            tooltip: 'Odoslať e-mailom',
            onClick: openSingleEmailDialog,
            hidden: (data: any) => data.status !== 'PREPARED',
          },
          {
            icon: 'pi pi-send',
            tooltip: 'Označiť ako odoslanú (bez e-mailu)',
            onClick: onMarkSent,
            hidden: (data: any) => data.status !== 'PREPARED',
          },
          {
            icon: 'pi pi-times',
            tooltip: 'Zrušiť',
            onClick: onCancel,
            hidden: (data: any) => data.status !== 'PREPARED',
          },
        ],
      })}

      <Dialog
        visible={singleDialog.open}
        header={`Odoslať upomienku e-mailom – ${singleDialog.row?.clientName || ''}`}
        modal
        style={{ width: '40rem' }}
        breakpoints={{ '960px': '75vw', '641px': '95vw' }}
        onHide={closeSingleEmailDialog}
        footer={
          <div>
            <Button label="Zrušiť" icon="pi pi-times" outlined onClick={closeSingleEmailDialog} disabled={loading} />
            <Button
              style={{ backgroundColor: '#e31818' }}
              label="Odoslať"
              icon="pi pi-envelope"
              onClick={onSendSingleEmail}
              loading={loading}
            />
          </div>
        }
      >
        <div className="flex flex-column gap-3">
          <div>
            <label className="block mb-1">Príjemca (e-mail)</label>
            <InputText
              className="w-full"
              value={singleDialog.email}
              onChange={e => setSingleDialog(s => ({ ...s, email: e.target.value }))}
              placeholder="napr. klient@example.com"
            />
          </div>
          <div>
            <label className="block mb-1">Predmet</label>
            <InputText
              className="w-full"
              value={singleDialog.subject}
              onChange={e => setSingleDialog(s => ({ ...s, subject: e.target.value }))}
            />
          </div>
          <div>
            <label className="block mb-1">Správa (HTML)</label>
            <InputTextarea
              className="w-full"
              rows={8}
              value={singleDialog.message}
              onChange={e => setSingleDialog(s => ({ ...s, message: e.target.value }))}
            />
          </div>
          <div className="text-sm text-color-secondary">
            PDF upomienky bude pripojené ako príloha. Po úspešnom odoslaní sa stav zmení na „Odoslaná".
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={bulkDialog.open}
        header={`Hromadné odoslanie e-mailov (${sendableSelected.length})`}
        modal
        style={{ width: '46rem' }}
        breakpoints={{ '960px': '75vw', '641px': '95vw' }}
        onHide={closeBulkDialog}
        footer={
          <div>
            <Button label="Zrušiť" icon="pi pi-times" outlined onClick={closeBulkDialog} disabled={loading} />
            <Button
              style={{ backgroundColor: '#e31818' }}
              label={`Odoslať ${sendableSelected.length} upomienok`}
              icon="pi pi-envelope"
              onClick={onSendBulk}
              loading={loading}
              disabled={!sendableSelected.length}
            />
          </div>
        }
      >
        <div className="flex flex-column gap-3">
          <div>
            <label className="block mb-1">Predmet</label>
            <InputText
              className="w-full"
              value={bulkDialog.subject}
              onChange={e => setBulkDialog(s => ({ ...s, subject: e.target.value }))}
            />
          </div>
          <div>
            <label className="block mb-1">Správa (HTML)</label>
            <InputTextarea
              className="w-full"
              rows={8}
              value={bulkDialog.message}
              onChange={e => setBulkDialog(s => ({ ...s, message: e.target.value }))}
            />
          </div>
          <div>
            <div className="font-semibold mb-1">Príjemcovia ({sendableSelected.length})</div>
            <div style={{ maxHeight: '12rem', overflowY: 'auto' }} className="border-1 surface-border border-round p-2 text-sm">
              {sendableSelected.map(r => (
                <div key={r.id}>
                  #{r.id} – {r.clientName || ''} &lt;{r.clientEmail}&gt; – {r.level}. úroveň
                </div>
              ))}
            </div>
          </div>
          {skippedSelected.length > 0 && (
            <div>
              <div className="font-semibold mb-1 text-orange-600">Preskočené ({skippedSelected.length})</div>
              <div style={{ maxHeight: '8rem', overflowY: 'auto' }} className="border-1 surface-border border-round p-2 text-sm">
                {skippedSelected.map(r => (
                  <div key={r.id}>
                    #{r.id} – {r.clientName || ''} – {r.status !== 'PREPARED' ? `stav „${r.status}"` : 'chýba e-mail klienta'}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="text-sm text-color-secondary">
            Každej upomienke bude pripojené jej vlastné PDF. Po odoslaní sa stav zmení na „Odoslaná".
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default UpomienkaTable;
